<div class="page-container" *ngIf="!loading && project">
  <!-- Header -->
  <div class="detail-header">
    <button mat-icon-button (click)="goBack()" matTooltip="Zurück">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <div class="header-info">
      <h1>{{project.name}}</h1>
      <span class="customer-label">
        <img *ngIf="project.customerLogoPath" [src]="getLogoUrl(project.customerLogoPath)" alt="Logo" class="header-customer-logo">
        {{project.customerName}}
      </span>
      <span class="status-badge" [ngClass]="{
        'status-planning': project.status === 'InPlanung',
        'status-active': project.status === 'InArbeit',
        'status-handover': project.status === 'Uebergeben',
        'status-done': project.status === 'Abgeschlossen',
        'status-archived': project.status === 'Archiviert'
      }">{{statusLabels[project.status]}}</span>
    </div>
    <div class="header-actions">
      <button mat-stroked-button (click)="exportAsJson()" matTooltip="Als JSON exportieren">
        <mat-icon>download</mat-icon> Export
      </button>
      <button mat-raised-button color="primary" (click)="editProject()">
        <mat-icon>edit</mat-icon> Bearbeiten
      </button>
    </div>
  </div>

  <!-- Info Cards -->
  <div class="info-cards">
    <div class="info-card" *ngIf="project.projectNumber">
      <mat-icon>tag</mat-icon>
      <div>
        <span class="info-label">Projektnummer</span>
        <span class="info-value">{{project.projectNumber}}</span>
      </div>
    </div>
    <div class="info-card" *ngIf="project.plcType">
      <mat-icon>memory</mat-icon>
      <div>
        <span class="info-label">SPS-Typ</span>
        <span class="info-value">{{project.plcType}}</span>
      </div>
    </div>
    <div class="info-card" *ngIf="project.plcFirmwareVersion">
      <mat-icon>system_update</mat-icon>
      <div>
        <span class="info-label">Firmware</span>
        <span class="info-value">{{project.plcFirmwareVersion}}</span>
      </div>
    </div>
    <div class="info-card" *ngIf="project.handoverDate">
      <mat-icon>event</mat-icon>
      <div>
        <span class="info-label">Übergabe</span>
        <span class="info-value">{{project.handoverDate | date:'dd.MM.yyyy'}}</span>
      </div>
    </div>
  </div>

  <!-- Hash Section -->
  <div class="hash-section" *ngIf="project.projectHash">
    <mat-icon>fingerprint</mat-icon>
    <div class="hash-content">
      <span class="hash-label">Projekt-Hash ({{project.hashAlgorithm || 'unbekannt'}})</span>
      <code class="hash-value">{{project.projectHash}}</code>
    </div>
  </div>

  <!-- Description / Notes -->
  <div class="notes-section" *ngIf="project.description || project.notes">
    <div *ngIf="project.description" class="note-block">
      <h4>Beschreibung</h4>
      <p>{{project.description}}</p>
    </div>
    <div *ngIf="project.notes" class="note-block">
      <h4>Notizen</h4>
      <p>{{project.notes}}</p>
    </div>
  </div>

  <!-- Tabs: Components & Documents -->
  <mat-tab-group animationDuration="200ms">
    <!-- Components Tab -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon>widgets</mat-icon>
        <span class="tab-label">Komponenten ({{project.components ? project.components.length : 0}})</span>
      </ng-template>

      <div class="tab-content">
        <div class="tab-header">
          <button mat-raised-button color="primary" (click)="addComponent()">
            <mat-icon>add</mat-icon> Komponente hinzufügen
          </button>
        </div>

        <div *ngIf="!project.components?.length" class="empty-tab">
          <mat-icon>widgets</mat-icon>
          <p>Noch keine Komponenten erfasst</p>
        </div>

        <table mat-table [dataSource]="project.components" *ngIf="project.components?.length" class="component-table">
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Name</th>
            <td mat-cell *matCellDef="let c">
              <div class="cell-primary">{{c.name}}</div>
              <div class="cell-secondary" *ngIf="c.partNumber">Art.-Nr.: {{c.partNumber}}</div>
            </td>
          </ng-container>

          <ng-container matColumnDef="version">
            <th mat-header-cell *matHeaderCellDef>Version</th>
            <td mat-cell *matCellDef="let c">{{c.version || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="componentType">
            <th mat-header-cell *matHeaderCellDef>Typ</th>
            <td mat-cell *matCellDef="let c">
              <span class="type-chip" *ngIf="c.componentType">{{c.componentType}}</span>
              <span *ngIf="!c.componentType">-</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="supplier">
            <th mat-header-cell *matHeaderCellDef>Lieferant</th>
            <td mat-cell *matCellDef="let c">{{c.supplier || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="isCritical">
            <th mat-header-cell *matHeaderCellDef>Kritisch</th>
            <td mat-cell *matCellDef="let c">
              <mat-icon *ngIf="c.isCritical" class="critical-icon">warning</mat-icon>
              <span *ngIf="!c.isCritical">-</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let c">
              <button mat-icon-button (click)="editComponent(c)" matTooltip="Bearbeiten">
                <mat-icon>edit</mat-icon>
              </button>
              <button mat-icon-button color="warn" (click)="deleteComponent(c)" matTooltip="Löschen">
                <mat-icon>delete</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        </table>
      </div>
    </mat-tab>

    <!-- Documents Tab -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon>description</mat-icon>
        <span class="tab-label">Dokumente ({{project.documents ? project.documents.length : 0}})</span>
      </ng-template>

      <div class="tab-content">
        <!-- Upload Section -->
        <div class="upload-section">
          <div class="upload-fields">
            <mat-form-field appearance="outline" class="upload-desc">
              <mat-label>Beschreibung</mat-label>
              <input matInput [(ngModel)]="uploadDescription" placeholder="Optionale Beschreibung">
            </mat-form-field>
            <mat-form-field appearance="outline" class="upload-type">
              <mat-label>Dokumenttyp</mat-label>
              <mat-select [(ngModel)]="uploadDocType">
                <mat-option value="">-</mat-option>
                <mat-option *ngFor="let dt of documentTypes" [value]="dt">{{dt}}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <button mat-raised-button color="primary" (click)="fileInput.click()" [disabled]="uploading">
            <mat-spinner *ngIf="uploading" diameter="18"></mat-spinner>
            <mat-icon *ngIf="!uploading">upload_file</mat-icon>
            Datei hochladen
          </button>
          <input #fileInput type="file" hidden (change)="onFileSelected($event)">
        </div>

        <div *ngIf="!project.documents?.length" class="empty-tab">
          <mat-icon>folder_open</mat-icon>
          <p>Noch keine Dokumente hochgeladen</p>
        </div>

        <!-- Document List -->
        <div class="document-list" *ngIf="project.documents?.length">
          <div class="document-item" *ngFor="let doc of project.documents">
            <div class="doc-icon">
              <mat-icon>insert_drive_file</mat-icon>
            </div>
            <div class="doc-info">
              <span class="doc-name">{{doc.originalFileName}}</span>
              <span class="doc-meta">
                {{formatFileSize(doc.fileSize)}}
                <span *ngIf="doc.documentType"> · {{doc.documentType}}</span>
                · {{doc.uploadedAt | date:'dd.MM.yyyy HH:mm'}}
              </span>
              <span class="doc-desc" *ngIf="doc.description">{{doc.description}}</span>
            </div>
            <div class="doc-actions">
              <button mat-icon-button *ngIf="isPreviewable(doc)" (click)="previewDocument(doc)" matTooltip="Vorschau">
                <mat-icon>visibility</mat-icon>
              </button>
              <button mat-icon-button (click)="downloadDocument(doc)" matTooltip="Herunterladen">
                <mat-icon>download</mat-icon>
              </button>
              <button mat-icon-button color="warn" (click)="deleteDocument(doc)" matTooltip="Löschen">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </mat-tab>

    <!-- Security Assessment Tab -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon>security</mat-icon>
        <span class="tab-label">Sicherheit
          <span *ngIf="project.securityAssessment" class="security-badge">
            {{project.securityAssessment.completedRequirements}}/{{project.securityAssessment.totalRequirements}}
          </span>
        </span>
      </ng-template>

      <div class="tab-content">
        <!-- No assessment yet -->
        <div *ngIf="!project.securityAssessment" class="empty-tab security-empty">
          <mat-icon>security</mat-icon>
          <p>Noch keine Sicherheitsbewertung durchgeführt</p>
          <button mat-raised-button color="primary" (click)="startSecurityCheck()">
            <mat-icon>play_arrow</mat-icon> Sicherheitscheck starten
          </button>
        </div>

        <!-- Assessment exists -->
        <div *ngIf="project.securityAssessment" class="security-content">
          <!-- Summary -->
          <div class="security-summary">
            <div class="sl-display target"
                 [style.background]="(slDescriptions[project.securityAssessment.targetSecurityLevel]?.color || '#999') + '15'">
              <span class="sl-label">Ziel-SL</span>
              <span class="sl-value" [style.color]="slDescriptions[project.securityAssessment.targetSecurityLevel]?.color">
                {{project.securityAssessment.targetSecurityLevel}}
              </span>
            </div>
            <div class="sl-display achieved"
                 [style.background]="(slDescriptions[project.securityAssessment.achievedSecurityLevel]?.color || '#999') + '15'">
              <span class="sl-label">Erreicht</span>
              <span class="sl-value" [style.color]="slDescriptions[project.securityAssessment.achievedSecurityLevel]?.color">
                {{project.securityAssessment.achievedSecurityLevel}}
              </span>
            </div>
            <div class="security-progress-area">
              <div class="security-progress-header">
                <span>Fortschritt: {{getSecurityProgress()}}%</span>
                <span>{{project.securityAssessment.completedRequirements}} / {{project.securityAssessment.totalRequirements}} erledigt</span>
              </div>
              <div class="security-progress-track">
                <div class="security-progress-fill" [style.width.%]="getSecurityProgress()"
                     [style.background]="getSecurityProgress() >= 80 ? '#22c55e' : getSecurityProgress() >= 50 ? '#eab308' : '#ef4444'"></div>
              </div>
            </div>
            <button mat-stroked-button (click)="startSecurityCheck()" class="rerun-btn">
              <mat-icon>refresh</mat-icon> Bewertung wiederholen
            </button>
          </div>

          <!-- Requirements Checklist by Category -->
          <div class="req-category" *ngIf="getRequirementsByCategory('NIS2').length > 0">
            <h4><mat-icon>policy</mat-icon> NIS 2 Anforderungen</h4>
            <div class="req-item" *ngFor="let req of getRequirementsByCategory('NIS2')"
                 [class.completed]="req.isCompleted">
              <mat-checkbox [checked]="req.isCompleted" (change)="toggleRequirement(req)">
                <div class="req-text">
                  <span class="req-title">{{req.title}}</span>
                  <span class="req-norm">{{req.normReference}}</span>
                </div>
              </mat-checkbox>
              <span *ngIf="req.isCompleted" class="req-done-info">
                <mat-icon>check_circle</mat-icon> {{req.completedBy}} · {{req.completedAt | date:'dd.MM.yyyy'}}
              </span>
            </div>
          </div>

          <div class="req-category" *ngIf="getRequirementsByCategory('FR').length > 0">
            <h4><mat-icon>shield</mat-icon> IEC 62443 Foundational Requirements</h4>
            <div class="req-item" *ngFor="let req of getRequirementsByCategory('FR')"
                 [class.completed]="req.isCompleted">
              <mat-checkbox [checked]="req.isCompleted" (change)="toggleRequirement(req)">
                <div class="req-text">
                  <span class="req-title">{{req.title}}</span>
                  <span class="req-norm">{{req.normReference}}</span>
                </div>
              </mat-checkbox>
              <span *ngIf="req.isCompleted" class="req-done-info">
                <mat-icon>check_circle</mat-icon> {{req.completedBy}} · {{req.completedAt | date:'dd.MM.yyyy'}}
              </span>
            </div>
          </div>

          <div class="req-category" *ngIf="getRequirementsByCategory('Dokument').length > 0">
            <h4><mat-icon>description</mat-icon> Fehlende Dokumente</h4>
            <div class="req-item" *ngFor="let req of getRequirementsByCategory('Dokument')"
                 [class.completed]="req.isCompleted">
              <mat-checkbox [checked]="req.isCompleted" (change)="toggleRequirement(req)">
                <div class="req-text">
                  <span class="req-title">{{req.title}}</span>
                  <span class="req-norm">{{req.normReference}}</span>
                </div>
              </mat-checkbox>
              <span *ngIf="req.isCompleted" class="req-done-info">
                <mat-icon>check_circle</mat-icon> {{req.completedBy}} · {{req.completedAt | date:'dd.MM.yyyy'}}
              </span>
            </div>
          </div>
        </div>
      </div>
    </mat-tab>

    <!-- Audit Trail Tab -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon>history</mat-icon>
        <span class="tab-label">Änderungsprotokoll ({{project.auditLogs ? project.auditLogs.length : 0}})</span>
      </ng-template>

      <div class="tab-content">
        <div *ngIf="!project.auditLogs?.length" class="empty-tab">
          <mat-icon>history</mat-icon>
          <p>Noch keine Änderungen protokolliert</p>
        </div>

        <div class="audit-timeline" *ngIf="project.auditLogs?.length">
          <div class="audit-entry" *ngFor="let log of project.auditLogs">
            <div class="audit-marker"></div>
            <div class="audit-content">
              <div class="audit-header">
                <span class="audit-action" [ngClass]="{
                  'action-create': log.action === 'Erstellt' || log.action === 'Hinzugefügt' || log.action === 'Hochgeladen',
                  'action-edit': log.action === 'Bearbeitet',
                  'action-delete': log.action === 'Gelöscht'
                }">{{log.action}}</span>
                <span class="audit-entity">{{log.entityType}}: {{log.entityName}}</span>
              </div>
              <div class="audit-details" *ngIf="log.details">{{log.details}}</div>
              <div class="audit-meta">
                <mat-icon>person</mat-icon> {{log.userName}}
                <mat-icon>schedule</mat-icon> {{log.timestamp | date:'dd.MM.yyyy HH:mm'}}
              </div>
            </div>
          </div>
        </div>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>

<!-- Loading -->
<div *ngIf="loading" class="loading-container">
  <mat-spinner diameter="48"></mat-spinner>
</div>
